/**
 * Pure TEA update for SplashPage: one Model / Msg / Cmd for the page.
 *
 * Nested chrome (new project, delete confirm, cloud restore, account, About)
 * is composed here so mutual exclusion and busy rules live in `update`, not as
 * peer `useTea` loops in the shell.
 *
 * Page data (recents, busy, cloud auth snapshot, beta nudge) lives on the same
 * Model so the Vue shell only holds `model` + `dispatch`.
 *
 * New-project transitions reuse `NewProjectDialog.update.ts` (also used by
 * EditorShell’s own single loop).
 *
 * Session / cloud / recent shapes are local so this module stays free of
 * `lib/cloud*` / `lib/tauri*` imports (ESLint pure-update rule).
 */
import { assertNever } from "../lib/assertNever";
import { isJust, isNothing, just, nothing, type Maybe } from "../lib/maybe";
import { narrowLayoutForWidth } from "../lib/narrowLayout";
import type { UpdateFn } from "../lib/tea";
import {
  initialNewProjectDialogModel,
  updateNewProjectDialog,
  type NewProjectDialogCmd,
  type NewProjectDialogModel,
  type NewProjectDialogMsg,
} from "./NewProjectDialog.update";
import {
  initialAboutLauncherModel,
  updateAboutLauncher,
  type AboutLauncherCmd,
  type AboutLauncherModel,
  type AboutLauncherMsg,
} from "./AboutPanel.update";

// --- Local page data shapes -----------------------------------------------

export type SplashRecentItem = {
  readonly path: string;
  readonly name: string;
  readonly kind: "project";
};

export type SplashCloudProject = {
  readonly id: string;
  readonly name: string;
  readonly clientProjectId: Maybe<string>;
  readonly lastBackedUpAt: Maybe<string>;
};

export type SplashBetaUpdate = {
  readonly current: string;
  readonly latest: string;
  readonly sourceRoot: string;
};

export type SplashAccountSession = {
  readonly email: string;
  readonly userId: string;
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly accessTokenExpiresIn: number;
  readonly savedAt: number;
};

// --- Delete confirm -------------------------------------------------------

export type SplashDeleteTarget = {
  readonly path: string;
  readonly name: string;
};

export type SplashDeleteModel = {
  readonly pending: Maybe<SplashDeleteTarget>;
};

export const initialSplashDeleteModel: SplashDeleteModel = {
  pending: nothing,
};

export type SplashDeleteMsg =
  | { readonly type: "Ask"; readonly target: SplashDeleteTarget }
  | { readonly type: "Cancel" }
  | { readonly type: "Commit" }
  | { readonly type: "DeleteSucceeded" }
  | { readonly type: "DeleteFailed" };

export type SplashDeleteCmd = {
  readonly kind: "delete";
  readonly path: string;
};

function updateSplashDelete(
  model: SplashDeleteModel,
  msg: SplashDeleteMsg,
): readonly [SplashDeleteModel, readonly SplashDeleteCmd[]] {
  switch (msg.type) {
    case "Ask":
      return [{ pending: just(msg.target) }, []];
    case "Cancel":
    case "DeleteSucceeded":
    case "DeleteFailed":
      return [initialSplashDeleteModel, []];
    case "Commit": {
      if (isNothing(model.pending)) return [model, []];
      return [model, [{ kind: "delete", path: model.pending.value.path }]];
    }
    default:
      return assertNever(msg);
  }
}

// --- Cloud restore --------------------------------------------------------

export type SplashCloudRestoreModel = {
  readonly open: boolean;
  readonly loading: boolean;
  readonly error: string;
  readonly emptyHint: string;
  readonly selectedId: Maybe<string>;
};

export const initialSplashCloudRestoreModel: SplashCloudRestoreModel = {
  open: false,
  loading: false,
  error: "",
  emptyHint: "",
  selectedId: nothing,
};

export type SplashCloudRestoreMsg =
  | { readonly type: "Open" }
  | { readonly type: "Cancel" }
  | {
      readonly type: "Listed";
      readonly projects: readonly SplashCloudProject[];
      readonly emptyHint: string;
      readonly autoSelectId: Maybe<string>;
    }
  | { readonly type: "ListFailed"; readonly error: string }
  | { readonly type: "Select"; readonly id: string }
  | { readonly type: "Commit" }
  | { readonly type: "RestoreSucceeded" }
  | { readonly type: "RestoreFailed"; readonly error: string }
  | { readonly type: "RestoreAborted" };

export type SplashCloudRestoreCmd =
  | { readonly kind: "listProjects" }
  | { readonly kind: "restore"; readonly projectId: string };

function updateSplashCloudRestore(
  model: SplashCloudRestoreModel,
  msg: SplashCloudRestoreMsg,
): readonly [SplashCloudRestoreModel, readonly SplashCloudRestoreCmd[]] {
  switch (msg.type) {
    case "Open":
      return [
        {
          open: true,
          loading: true,
          error: "",
          emptyHint: "",
          selectedId: nothing,
        },
        [{ kind: "listProjects" }],
      ];
    case "Cancel":
      return [initialSplashCloudRestoreModel, []];
    case "Listed":
      return [
        {
          ...model,
          loading: false,
          error: "",
          emptyHint: msg.emptyHint,
          selectedId: msg.autoSelectId,
        },
        [],
      ];
    case "ListFailed":
      return [
        {
          ...model,
          loading: false,
          error: msg.error,
          emptyHint: "",
        },
        [],
      ];
    case "Select":
      return [{ ...model, selectedId: just(msg.id), error: "" }, []];
    case "Commit": {
      if (isNothing(model.selectedId)) {
        return [{ ...model, error: "Select a project to restore." }, []];
      }
      return [
        { ...model, error: "" },
        [{ kind: "restore", projectId: model.selectedId.value }],
      ];
    }
    case "RestoreSucceeded":
      return [initialSplashCloudRestoreModel, []];
    case "RestoreFailed":
      return [{ ...model, error: msg.error }, []];
    case "RestoreAborted":
      return [model, []];
    default:
      return assertNever(msg);
  }
}

// --- Account flyout -------------------------------------------------------

export type SplashAccountModel = {
  readonly open: boolean;
  /** Keep the flyout mounted briefly after close for exit animation. */
  readonly present: boolean;
};

export const initialSplashAccountModel: SplashAccountModel = {
  open: false,
  present: false,
};

export type SplashAccountMsg =
  | { readonly type: "Open" }
  | { readonly type: "Close" }
  | { readonly type: "FlyoutClosed" };

function updateSplashAccount(
  model: SplashAccountModel,
  msg: SplashAccountMsg,
): readonly [SplashAccountModel, readonly never[]] {
  switch (msg.type) {
    case "Open":
      return [{ open: true, present: true }, []];
    case "Close":
      return [{ ...model, open: false }, []];
    case "FlyoutClosed":
      return [{ open: false, present: false }, []];
    default:
      return assertNever(msg);
  }
}

// --- Page composition -----------------------------------------------------

export type SplashPageModel = {
  readonly recents: readonly SplashRecentItem[];
  readonly library: readonly SplashRecentItem[];
  /**
   * Running in the native Android/iOS shell. Drives file and platform
   * behaviour: the app-local project library, in-app project creation, and
   * no folder picker.
   */
  readonly nativeMobileShell: boolean;
  /** Splash width is below the narrow-layout breakpoint (ZW-276). */
  readonly narrowLayout: boolean;
  readonly busy: boolean;
  readonly busyLabel: string;
  readonly splashError: string;
  readonly cloudRestoreAvailable: boolean;
  readonly cloudProjects: readonly SplashCloudProject[];
  readonly workspaceUnlocked: boolean;
  readonly betaUpdate: Maybe<SplashBetaUpdate>;
  readonly betaUpdateBusy: boolean;
  readonly betaUpdateError: string;
  readonly accountSession: Maybe<SplashAccountSession>;
  readonly cloudReauthNeeded: boolean;
  readonly newProject: NewProjectDialogModel;
  readonly delete: SplashDeleteModel;
  readonly cloudRestore: SplashCloudRestoreModel;
  readonly account: SplashAccountModel;
  /** Version label → About window (desktop) or pop-up (web, mobile). */
  readonly about: AboutLauncherModel;
};

/**
 * Phone layout on the splash page: the native shell, or any window narrower
 * than the breakpoint. Layout only; file and dialog behaviour reads
 * `nativeMobileShell`.
 */
export function splashMobileLayout(model: SplashPageModel): boolean {
  return model.nativeMobileShell || model.narrowLayout;
}

export type SplashPageSeed = {
  readonly accountSession: Maybe<SplashAccountSession>;
  readonly cloudReauthNeeded: boolean;
  /** Window width at boot, so a narrow window opens in the phone layout. */
  readonly initialWidth: number;
};

export function initialSplashPageModel(
  seed: SplashPageSeed = {
    accountSession: nothing,
    cloudReauthNeeded: false,
    initialWidth: 0,
  },
): SplashPageModel {
  return {
    recents: [],
    library: [],
    nativeMobileShell: false,
    narrowLayout: narrowLayoutForWidth(false, seed.initialWidth),
    busy: false,
    busyLabel: "",
    splashError: "",
    cloudRestoreAvailable: false,
    cloudProjects: [],
    workspaceUnlocked: false,
    betaUpdate: nothing,
    betaUpdateBusy: false,
    betaUpdateError: "",
    accountSession: seed.accountSession,
    cloudReauthNeeded: seed.cloudReauthNeeded,
    newProject: initialNewProjectDialogModel,
    delete: initialSplashDeleteModel,
    cloudRestore: initialSplashCloudRestoreModel,
    account: initialSplashAccountModel,
    about: initialAboutLauncherModel,
  };
}

export type SplashPageMsg =
  | { readonly type: "NewProject"; readonly msg: NewProjectDialogMsg }
  | { readonly type: "Delete"; readonly msg: SplashDeleteMsg }
  | { readonly type: "CloudRestore"; readonly msg: SplashCloudRestoreMsg }
  | { readonly type: "Account"; readonly msg: SplashAccountMsg }
  | { readonly type: "About"; readonly msg: AboutLauncherMsg }
  | { readonly type: "MobileShellDetected"; readonly mobileShell: boolean }
  | { readonly type: "SplashWidthMeasured"; readonly width: number }
  | {
      readonly type: "RecentsLoaded";
      readonly recents: readonly SplashRecentItem[];
      readonly library: readonly SplashRecentItem[];
    }
  | {
      readonly type: "BusyStarted";
      readonly label: string;
      readonly clearError: boolean;
    }
  | { readonly type: "BusyEnded" }
  | { readonly type: "SplashErrorSet"; readonly error: string }
  | { readonly type: "SplashErrorCleared" }
  | { readonly type: "WorkspaceAccessChecked"; readonly unlocked: boolean }
  | {
      readonly type: "CloudAuthSynced";
      readonly session: Maybe<SplashAccountSession>;
      readonly workspaceUnlocked: boolean;
      readonly cloudRestoreAvailable: boolean;
      readonly cloudReauthNeeded: boolean;
    }
  | { readonly type: "BetaUpdateOffered"; readonly info: SplashBetaUpdate }
  | { readonly type: "BetaUpdateDismissed" }
  | { readonly type: "BetaUpdateConfirmStarted" }
  | { readonly type: "BetaUpdateErrorSet"; readonly error: string }
  | { readonly type: "BetaUpdateConfirmEnded" };

export type SplashPageCmd =
  | { readonly scope: "newProject"; readonly cmd: NewProjectDialogCmd }
  | { readonly scope: "delete"; readonly cmd: SplashDeleteCmd }
  | { readonly scope: "cloudRestore"; readonly cmd: SplashCloudRestoreCmd }
  | { readonly scope: "about"; readonly cmd: AboutLauncherCmd };

function mapNewProjectCmds(
  cmds: readonly NewProjectDialogCmd[],
): SplashPageCmd[] {
  return cmds.map((cmd) => ({ scope: "newProject" as const, cmd }));
}

function mapDeleteCmds(cmds: readonly SplashDeleteCmd[]): SplashPageCmd[] {
  return cmds.map((cmd) => ({ scope: "delete" as const, cmd }));
}

function mapCloudRestoreCmds(
  cmds: readonly SplashCloudRestoreCmd[],
): SplashPageCmd[] {
  return cmds.map((cmd) => ({ scope: "cloudRestore" as const, cmd }));
}

/** Clear sibling chrome when opening a surface (keep in-flight new-project). */
function clearSiblings(
  model: SplashPageModel,
  keep: "newProject" | "delete" | "cloudRestore" | "account" | "about",
): SplashPageModel {
  return {
    ...model,
    newProject:
      keep === "newProject" || model.newProject.busy
        ? model.newProject
        : initialNewProjectDialogModel,
    delete: keep === "delete" ? model.delete : initialSplashDeleteModel,
    cloudRestore:
      keep === "cloudRestore"
        ? model.cloudRestore
        : initialSplashCloudRestoreModel,
    account: keep === "account" ? model.account : initialSplashAccountModel,
    about:
      keep === "about"
        ? model.about
        : { ...model.about, open: false, checkOnOpen: false },
  };
}

function chromeBusy(model: SplashPageModel): boolean {
  return model.newProject.busy;
}

function clearSplashErrorOnOpen(model: SplashPageModel): SplashPageModel {
  return { ...model, splashError: "" };
}

export function updateSplashPage(
  model: SplashPageModel,
  msg: SplashPageMsg,
): readonly [SplashPageModel, readonly SplashPageCmd[]] {
  switch (msg.type) {
    case "NewProject": {
      if (msg.msg.type === "Open") {
        if (chromeBusy(model)) return [model, []];
        const base = clearSplashErrorOnOpen(clearSiblings(model, "newProject"));
        const [newProject, cmds] = updateNewProjectDialog(
          base.newProject,
          msg.msg,
        );
        return [{ ...base, newProject }, mapNewProjectCmds(cmds)];
      }
      const [newProject, cmds] = updateNewProjectDialog(
        model.newProject,
        msg.msg,
      );
      return [{ ...model, newProject }, mapNewProjectCmds(cmds)];
    }
    case "Delete": {
      if (msg.msg.type === "Ask") {
        if (chromeBusy(model)) return [model, []];
        const base = clearSplashErrorOnOpen(clearSiblings(model, "delete"));
        const [del, cmds] = updateSplashDelete(base.delete, msg.msg);
        return [{ ...base, delete: del }, mapDeleteCmds(cmds)];
      }
      const [del, cmds] = updateSplashDelete(model.delete, msg.msg);
      return [{ ...model, delete: del }, mapDeleteCmds(cmds)];
    }
    case "CloudRestore": {
      if (msg.msg.type === "Open") {
        if (chromeBusy(model)) return [model, []];
        const base = clearSplashErrorOnOpen(
          clearSiblings(model, "cloudRestore"),
        );
        const [cloudRestore, cmds] = updateSplashCloudRestore(
          base.cloudRestore,
          msg.msg,
        );
        return [
          { ...base, cloudRestore, cloudProjects: [] },
          mapCloudRestoreCmds(cmds),
        ];
      }
      if (msg.msg.type === "Listed") {
        const [cloudRestore, cmds] = updateSplashCloudRestore(
          model.cloudRestore,
          msg.msg,
        );
        return [
          { ...model, cloudRestore, cloudProjects: msg.msg.projects },
          mapCloudRestoreCmds(cmds),
        ];
      }
      if (msg.msg.type === "ListFailed") {
        const [cloudRestore, cmds] = updateSplashCloudRestore(
          model.cloudRestore,
          msg.msg,
        );
        return [
          { ...model, cloudRestore, cloudProjects: [] },
          mapCloudRestoreCmds(cmds),
        ];
      }
      if (msg.msg.type === "Cancel" || msg.msg.type === "RestoreSucceeded") {
        const [cloudRestore, cmds] = updateSplashCloudRestore(
          model.cloudRestore,
          msg.msg,
        );
        return [
          { ...model, cloudRestore, cloudProjects: [] },
          mapCloudRestoreCmds(cmds),
        ];
      }
      const [cloudRestore, cmds] = updateSplashCloudRestore(
        model.cloudRestore,
        msg.msg,
      );
      return [{ ...model, cloudRestore }, mapCloudRestoreCmds(cmds)];
    }
    case "Account": {
      if (msg.msg.type === "Open") {
        if (chromeBusy(model)) return [model, []];
        const base = clearSplashErrorOnOpen(clearSiblings(model, "account"));
        const [account] = updateSplashAccount(base.account, msg.msg);
        return [{ ...base, account }, []];
      }
      const [account] = updateSplashAccount(model.account, msg.msg);
      return [{ ...model, account }, []];
    }
    case "About": {
      const opensDialog =
        msg.msg.type === "Open" && model.about.host === "dialog";
      if (opensDialog && chromeBusy(model)) return [model, []];
      const base = opensDialog ? clearSiblings(model, "about") : model;
      const [about, cmds] = updateAboutLauncher(base.about, msg.msg);
      return [
        { ...base, about },
        cmds.map((cmd) => ({ scope: "about" as const, cmd })),
      ];
    }
    case "MobileShellDetected":
      return [{ ...model, nativeMobileShell: msg.mobileShell }, []];
    case "SplashWidthMeasured": {
      const narrowLayout = narrowLayoutForWidth(model.narrowLayout, msg.width);
      if (narrowLayout === model.narrowLayout) return [model, []];
      return [{ ...model, narrowLayout }, []];
    }
    case "RecentsLoaded":
      return [{ ...model, recents: msg.recents, library: msg.library }, []];
    case "BusyStarted":
      return [
        {
          ...model,
          busy: true,
          busyLabel: msg.label,
          splashError: msg.clearError ? "" : model.splashError,
        },
        [],
      ];
    case "BusyEnded":
      return [{ ...model, busy: false, busyLabel: "" }, []];
    case "SplashErrorSet":
      return [{ ...model, splashError: msg.error }, []];
    case "SplashErrorCleared":
      return [{ ...model, splashError: "" }, []];
    case "WorkspaceAccessChecked":
      return [
        {
          ...model,
          workspaceUnlocked: msg.unlocked,
          splashError:
            msg.unlocked && model.splashError.includes("activate a license")
              ? ""
              : model.splashError,
        },
        [],
      ];
    case "CloudAuthSynced": {
      const splashError =
        msg.workspaceUnlocked === true &&
        model.splashError.includes("activate a license")
          ? ""
          : model.splashError;
      return [
        {
          ...model,
          accountSession: msg.session,
          workspaceUnlocked: msg.workspaceUnlocked,
          cloudRestoreAvailable: msg.cloudRestoreAvailable,
          cloudReauthNeeded: msg.cloudReauthNeeded,
          splashError,
        },
        [],
      ];
    }
    case "BetaUpdateOffered":
      return [{ ...model, betaUpdate: just(msg.info) }, []];
    case "BetaUpdateDismissed":
      return [{ ...model, betaUpdate: nothing, betaUpdateError: "" }, []];
    case "BetaUpdateConfirmStarted":
      return [{ ...model, betaUpdateBusy: true, betaUpdateError: "" }, []];
    case "BetaUpdateErrorSet":
      return [{ ...model, betaUpdateError: msg.error }, []];
    case "BetaUpdateConfirmEnded":
      return [{ ...model, betaUpdateBusy: false }, []];
    default:
      return assertNever(msg);
  }
}

export const updateSplashPageFn: UpdateFn<
  SplashPageModel,
  SplashPageMsg,
  SplashPageCmd
> = updateSplashPage;

/** True when a modal/flyout should swallow global splash shortcuts. */
export function splashChromeBlocksShortcuts(model: SplashPageModel): boolean {
  return (
    model.newProject.open ||
    model.cloudRestore.open ||
    model.account.open ||
    model.about.open ||
    isJust(model.delete.pending)
  );
}
